import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MessageOriginatingSocialCommentDto } from "@multi-chat/shared";
import {
  ExternalLink as ExternalLinkIcon,
  Image as ImageIcon,
  MessageSquareText as CommentIcon,
} from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { formatConversationFullDate } from "../conversation-time.js";
import { buildPostCommentsCommentPath } from "./comments-url-state.js";

/**
 * Verified Private Reply origin card. Only render when the API supplied a real
 * originatingSocialComment FK payload — never invent comment↔DM links client-side.
 */
export function CommentOriginBanner({
  origin,
  className,
  density = "thread",
}: {
  origin: MessageOriginatingSocialCommentDto;
  className?: string;
  density?: "thread" | "details";
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const author =
    origin.authorDisplayName?.trim() ||
    (origin.authorUsername
      ? `@${origin.authorUsername}`
      : t("inbox.comments.author.unknown"));
  const authorHandle =
    origin.authorDisplayName?.trim() && origin.authorUsername?.trim()
      ? origin.authorUsername.trim()
      : null;
  const viewPath = buildPostCommentsCommentPath(origin.commentId);
  const when = formatConversationFullDate(origin.platformCreatedAt, i18n.language);

  return (
    <aside
      className={cn(
        "rounded-[12px] border border-inbox-line bg-surface text-start",
        density === "thread" ? "mx-3 mb-1.5 max-w-[min(32rem,90%)] px-3 py-2.5" : "px-3 py-2.5",
        className,
      )}
      aria-label={t("inbox.comments.origin.aria")}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-[rgba(66,165,129,0.12)] text-[#2f7a5c]">
          <CommentIcon size={16} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <ChannelIdentityBadge platformType={origin.platformType} size="inline" />
            <p className="m-0 text-[0.75rem] font-semibold text-inbox-strong">
              {t("inbox.comments.origin.title")}
            </p>
          </div>
          <p className="m-0 mt-1 text-[0.8125rem] leading-snug text-inbox-text wrap-anywhere" dir="auto">
            {origin.content.trim().length > 0
              ? origin.content
              : t("inbox.comments.emptyBody")}
          </p>
          <p className="m-0 mt-1 text-[0.6875rem] text-inbox-muted">
            <span dir="auto">{author}</span>
            {authorHandle ? (
              <>
                <span aria-hidden="true"> · </span>
                <LtrValue>@{authorHandle}</LtrValue>
              </>
            ) : null}
            <span aria-hidden="true"> · </span>
            <time dateTime={origin.platformCreatedAt}>{when}</time>
          </p>
          {(origin.postCaptionPreview || origin.postMediaPreviewUrl) && (
            <div className="mt-2 flex gap-2 rounded-[10px] border border-inbox-line-soft bg-canvas/60 p-1.5">
              <div className="size-10 shrink-0 overflow-hidden rounded-md bg-inbox-line-soft">
                {origin.postMediaPreviewUrl ? (
                  <img
                    src={origin.postMediaPreviewUrl}
                    alt=""
                    className="size-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <span className="grid size-full place-items-center text-inbox-muted">
                    <ImageIcon size={14} aria-hidden="true" />
                  </span>
                )}
              </div>
              <p
                className="m-0 min-w-0 flex-1 self-center truncate text-[0.75rem] text-inbox-muted"
                dir="auto"
              >
                {origin.postCaptionPreview ?? t("inbox.comments.post.noCaption")}
              </p>
            </div>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to={viewPath}>{t("inbox.comments.origin.viewComment")}</Link>
            </Button>
            {origin.permalinkUrl || origin.postPermalinkUrl ? (
              <a
                href={origin.permalinkUrl ?? origin.postPermalinkUrl ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[0.75rem] font-semibold text-primary no-underline hover:underline"
              >
                <LtrValue>{t("inbox.comments.origin.openExternal")}</LtrValue>
                <ExternalLinkIcon size={12} aria-hidden="true" />
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </aside>
  );
}
